import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { WEDDING_CITIES, WEDDING_DEFAULT_CITY } from "@/weddingsConfig";
import WeddingsFooterFull from "@/components/WeddingsFooterFull";

import { getLang } from "@/components/weddings/shared";
import { setLangInWindowUrl } from "@/utils/seoLang";
import SeoTags from "@/components/weddings/SeoTags";
import TopNav from "@/components/weddings/TopNav";
import Hero from "@/components/weddings/Hero";
import HookSection from "@/components/weddings/HookSection";
import PartnersMarquee from "@/components/weddings/PartnersMarquee";
import ElementsSection from "@/components/weddings/ElementsSection";
import GallerySection from "@/components/weddings/GallerySection";
import PackagesSection from "@/components/weddings/PackagesSection";
import CompareTable from "@/components/weddings/CompareTable";
import TestimonialsSection from "@/components/weddings/TestimonialsSection";
import SeasonsSection from "@/components/weddings/SeasonsSection";
import ProcessSection from "@/components/weddings/ProcessSection";
import VenuesSection from "@/components/weddings/VenuesSection";
import FaqSection from "@/components/weddings/FaqSection";
import SeoContentSection from "@/components/weddings/SeoContentSection";
import RelatedArticlesSection from "@/components/weddings/RelatedArticlesSection";
import LeadFormSection from "@/components/weddings/LeadFormSection";
import BottomCta from "@/components/weddings/BottomCta";

export default function WeddingsPage() {
  const { citySlug } = useParams();
  const navigate = useNavigate();
  const city = WEDDING_CITIES[citySlug];
  const allowedLangs = city?.languages || ["ru", "en"];
  const [lang, setLang] = useState(() => getLang(allowedLangs));

  useEffect(() => {
    if (!city && citySlug) navigate(`/weddings/${WEDDING_DEFAULT_CITY}`, { replace: true });
  }, [city, citySlug, navigate]);

  // Re-sync lang when URL changes (back/forward, hreflang nav, direct city switch)
  useEffect(() => {
    const sync = () => setLang(getLang(allowedLangs));
    sync();
    window.addEventListener("popstate", sync);
    return () => window.removeEventListener("popstate", sync);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [citySlug, allowedLangs.join(",")]);

  // If lang was auto-detected (URL has no ?lang= but resolved !== "ru"),
  // sync it into the URL so canonical and share-links match.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const urlLang = new URLSearchParams(window.location.search).get("lang");
    if (urlLang === lang) return;
    if (lang === "ru" && !urlLang) return;
    setLangInWindowUrl(lang);
  }, [lang]);

  const switchLang = () => {
    const langs = city.languages || ["ru", "en"];
    const idx = langs.indexOf(lang);
    const next = langs[(idx + 1) % langs.length];
    setLang(next);
    setLangInWindowUrl(next); // also persists to localStorage
  };

  if (!city) return null;
  const t = city.seo[lang] || city.seo.ru;

  return (
    <div className="bg-white" data-testid="weddings-page">
      <SeoTags city={city} lang={lang} t={t} />
      <TopNav lang={lang} switchLang={switchLang} citySlug={city.slug} />
      <Hero city={city} t={t} />
      <HookSection t={t} />
      <PartnersMarquee t={t} />
      <ElementsSection t={t} />
      <GallerySection city={city} lang={lang} t={t} />
      <PackagesSection t={t} />
      <CompareTable t={t} />
      <TestimonialsSection t={t} />
      <SeasonsSection t={t} />
      <ProcessSection t={t} />
      <VenuesSection city={city} t={t} />
      <FaqSection t={t} />
      <SeoContentSection t={t} />
      <RelatedArticlesSection city={city} lang={lang} t={t} />
      <LeadFormSection city={city} lang={lang} t={t} />
      <BottomCta city={city} t={t} />
      <WeddingsFooterFull lang={lang} />
    </div>
  );
}
